/**
 * Segmented control — initializes behavior on existing HTML markup.
 *
 * --- Example — Simple form selector ---
 *
 *   <fieldset class="hq-segmented-control js-segmented-control">
 *     <legend class="hq-visually-hidden">Selectionnez un type de client</legend>
 *     <label class="hq-segmented-control__button">
 *       <input type="radio" name="type-client" value="residentiel" checked>
 *       Résidentiel
 *     </label>
 *     <label class="hq-segmented-control__button">
 *       <input type="radio" name="type-client" value="affaires">
 *       Affaires
 *     </label>
 *   </fieldset>
 *
 * Keyboard navigation is handled natively for the radio-group pattern.
 *
 * @param {Object}      options
 * @param {HTMLElement} options.el           - The container element.
 * @param {string}     [options.activeValue] - Override the initially active value.
 *                                             Falls back to the checked radio → first item.
 * @param {string}     [options.param]       - URL query-parameter key. When set, reads the
 *                                             initial value from the URL, updates it on change,
 *                                             and syncs on popstate.
 * @param {Function}   [options.onChange]    - Called with `(value)` on selection change.
 * @param {Function}   [options.onInit]      - Called once with resolved initial value.
 * @returns {{ setValue: (value: string) => void, getValue: () => string } | null}
 */

export function initSegmentedControl({ el, activeValue, param, onChange, onInit } = {}) {
  /** @type {HTMLInputElement[]} */
  const inputs = [...el.querySelectorAll('input[type="radio"]')];

  if (inputs.length === 0) {
    console.warn('initSegmentedControl: no radio inputs found inside', el);
    return null;
  }

  const initialValue = activeValue
    ?? (param ? getParam(param) : null)
    ?? el.querySelector('input[type="radio"]:checked')?.value
    ?? inputs[0].value;

  const defaultValue = inputs[0].value;
  let currentValue = defaultValue;

  setValue(initialValue);
  onInit?.(currentValue);

  if (param) {
    window.addEventListener('popstate', () => {
      const value = getParam(param) ?? defaultValue;
      setValue(value);
      onChange?.(value);
    });
  }

  inputs.forEach((input) => {
    input.addEventListener('change', () => {
      setValue(input.value);
      if (param) setParam(param, input.value, { defaultValue });
      onChange?.(input.value);
    });
  });

  /**
   * Activate a segment by value.
   * Checks the radio input. Does NOT call onChange.
   * @param {string} value
   */
  function setValue(value) {
    const resolved = inputs.some((input) => input.value === value)
      ? value
      : inputs[0].value;

    currentValue = resolved;

    inputs.forEach((input) => {
      input.checked = input.value === resolved;
    });
  }

  return {
    setValue,
    getValue: () => currentValue,
  };
}

/**
 * Get URL query parameter value
 * @param {string} key
 * @returns {string | null}
 */
function getParam(key) {
  const params = new URLSearchParams(window.location.search);
  return params.get(key);
}

/**
 * Set URL query parameter
 * @param {string} key
 * @param {string} value
 * @param {Object} options
 * @param {string} [options.defaultValue]
 */
function setParam(key, value, { defaultValue } = {}) {
  const params = new URLSearchParams(window.location.search);
  if (value === defaultValue) {
    params.delete(key);
  } else {
    params.set(key, value);
  }
  const query = params.toString();
  const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
  window.history.replaceState({}, '', url);
}
